import { AppState } from "store";
import { useSelector } from "react-redux";
//#region types
import { FormEnums, StyleEnums } from "models/enums";
import { AutoCompleteExtraProps, GenericFormSection } from "models/propTypes/genericFormProps";
import { FieldConstants, FormFieldConstants, IconDefinitionConstants, InvoiceConstants } from "utils/constants";
import { TimeReportingProjectFormTypes } from "models/formTypes";
import { TimeReportingProjectFormViewProps } from "models/propTypes/timeReportingProjectPropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { MessageBubbleDetails } from "models/propTypes/messagePropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { FormikProps } from "formik";
//#endregion
//#region components
import useTimeReportingHelpers from "./useTimeReportingHelpers";
import { useCustomerSearch, useReferencePersonSearch } from "components/customer";
import { useTimeReportingProjectService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

function useGeneralInfoFields(
  props: TimeReportingProjectFormViewProps
): GenericFormSection<TimeReportingProjectFormTypes.Create>[] {
  const timeReportingProjectService = useTimeReportingProjectService();
  const {
    customerId,
    onCustomerSelected,
    onCustomerAdd,
    onAddReferencePerson,
    onIsInternalProjectChange,
    onDuplicateCheck,
    topCustomers,
    topReferencePersons,
    isCreate,
  } = props;

  const { searchReferencePersons } = useReferencePersonSearch(customerId);
  const searchCustomers = useCustomerSearch();

  const dimensionMaps = useSelector((store: AppState) => {
    return store.common.dimensions.allDimensions;
  });

  const checkProjectNameDimensionAlreadyExists = async (values: TimeReportingProjectFormTypes.Create) => {
    if (!isCreate) {
      return;
    }

    const dimensions = dimensionMaps?.find(
      (item) => item.key.type === WintSuperkollAPIModel.DimensionCompatibilityType.Project
    )?.value;
    const existingNameDimensions =
      dimensions?.filter((c) => c.id !== undefined && c.name?.toLowerCase() === values.projectName?.toLowerCase()) ??
      [];
    const result = existingNameDimensions.length > 0;

    const message: MessageBubbleDetails | undefined = result
      ? {
          type: StyleEnums.DisplayType.Warning,
          message: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.TimeReportingProject.DimensionAlreadyExists
          ),
        }
      : undefined;

    return message;
  };

  const { isMemberActive } = useTimeReportingHelpers();

  const onDatesChange = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<TimeReportingProjectFormTypes.Create>
  ) => {
    const { values, setFieldValue } = formikProps;
    const updatedMembers = values.projectMembers?.map((member) => ({
      ...member,
      isTimeReportingActive: isMemberActive(
        values.projectStartDate,
        values.projectEndDate,
        member.startDate,
        member.endDate
      ),
    }));
    if (JSON.stringify(values.projectMembers) !== JSON.stringify(updatedMembers)) {
      setFieldValue(FormFieldConstants.TimeReportingProject.Create.ProjectMembers, updatedMembers);
    }
  };

  return [
    {
      label: undefined,
      type: FormEnums.FormSectionType.FIELDGROUPSECTION,
      icon: IconDefinitionConstants.WMoney,
      innerFields: {
        projectName: {
          label: TranslationKeys.TimeReportingProject.TimeReportingProjectName,
          descriptionText: TranslationKeys.TimeReportingProject.TimeReportingProjectNameDescription,
          type: FormEnums.FormFieldType.TEXT,
          onChangeHandler: onDuplicateCheck,
          customMessageHandler: checkProjectNameDimensionAlreadyExists,
        },

        projectManagerName: {
          label: TranslationKeys.TimeReportingProject.ProjectManager,
          descriptionText: TranslationKeys.TimeReportingProject.ProjectManagerDescription,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          icon: IconDefinitionConstants.WUser,
          extraProps: {
            items: (searchStr: string) =>
              timeReportingProjectService.searchAvailableProjectManagersAutoComplete(searchStr, false),
            referenceFieldName: FormFieldConstants.TimeReportingProject.Create.ProjectManagerId,
          } as AutoCompleteExtraProps<TimeReportingProjectFormTypes.Create>,
        },
        projectDescription: {
          label: TranslationKeys.Common.Description,
          descriptionText: TranslationKeys.TimeReportingProject.ProjectDescriptionDescription,
          type: FormEnums.FormFieldType.TEXTAREA,
        },
        projectStartDate: {
          label: TranslationKeys.TimeReportingProject.ProjectStartDate,
          descriptionText: TranslationKeys.TimeReportingProject.ProjectStartDateDescription,
          type: FormEnums.FormFieldType.DATE,
          onChangeHandler: onDatesChange,
        },
        projectEndDate: {
          label: TranslationKeys.TimeReportingProject.ProjectEndDate,
          descriptionText: TranslationKeys.TimeReportingProject.ProjectEndDateDescription,
          type: FormEnums.FormFieldType.DATE,
          onChangeHandler: onDatesChange,
        },
        isInternalProject: {
          label: TranslationKeys.TimeReportingProject.IsInternalProject,
          descriptionText: TranslationKeys.TimeReportingProject.IsInternalProjectDescription,
          type: FormEnums.FormFieldType.TOGGLE,
          onChangeHandler: onIsInternalProjectChange,
        },
      },
    },
    {
      label: TranslationKeys.TimeReportingProject.CustomerInformation,
      type: FormEnums.FormSectionType.FIELDGROUPSECTION,
      icon: IconDefinitionConstants.WCompany,
      hidden: {
        referenceFieldName: FormFieldConstants.TimeReportingProject.Create.IsInternalProject,
        hiddenWhenEqualTo: true,
      },
      innerFields: {
        customerName: {
          label: TranslationKeys.Invoice.Customer,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          icon: IconDefinitionConstants.WCompany,
          descriptionText: TranslationKeys.Invoice.SearchCustomerOrCreate,
          extraProps: {
            referenceFieldName: FormFieldConstants.TimeReportingProject.Create.CustomerId,
            onItemSelect: onCustomerSelected,
            suggestedItems: topCustomers,
            items: searchCustomers,
            actions: [
              {
                id: InvoiceConstants.AddCustomerAction,
                value: LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Invoice.SaveValueAsCustomer,
                  undefined,
                  { key: FieldConstants.ValueTemplateText }
                ),
                icon: IconDefinitionConstants.WPlus,
                onActionClicked: async (value: string) => {
                  onCustomerAdd(value);
                  return {} as AutocompleteListItem;
                },
                enablePropagationOnActionClick: true,
                actionSuccessMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Saved),
                hide: (options: AutocompleteListItem[], value?: string, isValueFromOptions?: boolean) =>
                  !value || isValueFromOptions || options.some((op) => op.value === value),
              },
            ],
          } as AutoCompleteExtraProps<TimeReportingProjectFormTypes.Create>,
        },
        invoiceReferenceName: {
          label: TranslationKeys.Invoice.CustomerReference,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          icon: IconDefinitionConstants.WBusinessCard,
          descriptionText: TranslationKeys.TimeReportingProject.CustomerReferencePersonDescription,
          disabled: (values: TimeReportingProjectFormTypes.Create) => values.loadingCustomer,
          extraProps: {
            referenceFieldName: FormFieldConstants.TimeReportingProject.Create.InvoiceReferenceId,
            suggestedItems: topReferencePersons,
            items: searchReferencePersons,
            actions: [
              {
                id: FormFieldConstants.TimeReportingProject.Create.InvoiceReferenceName,
                value: LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.TimeReportingProject.SaveValueAsReference,
                  undefined,
                  { key: FieldConstants.ValueTemplateText }
                ),
                icon: IconDefinitionConstants.WPlus,
                onActionClicked: async (name: string) => {
                  onAddReferencePerson(name);
                  return {} as AutocompleteListItem;
                },
                actionSuccessMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Saved),
                hide: (options: AutocompleteListItem[], value?: string, isValueFromOptions?: boolean) =>
                  !value || isValueFromOptions || options.some((op) => op.value === value),
              },
            ],
          } as AutoCompleteExtraProps<TimeReportingProjectFormTypes.Create>,
        },
      },
    },
    {
      label: TranslationKeys.Common.Tags,
      type: FormEnums.FormSectionType.FIELDGROUPSECTION,
      icon: IconDefinitionConstants.WHashtag,
      innerFields: {
        dimensionReadonly: {
          label: TranslationKeys.TimeReportingProject.TimeReportingProjects,
          type: FormEnums.FormFieldType.SELECTIONREADONLYFIELD,
        },
      },
    },
  ];
}

export default useGeneralInfoFields;
